import { CircleAlert, CircleCheck, TriangleAlert } from "lucide-react";
import { useTranslate } from "ra-core";

import { cn } from "@/lib/utils";

import { adviseButtonLabel, type ButtonIntentStatus } from "./botButtonIntents";

/**
 * Tells the operator what Felix will do when a recipient taps this button.
 *
 * A quick-reply label IS the command the bot receives, so an unrecognised label
 * silently dead-ends the recipient. This turns that invisible failure into
 * feedback while the label is being typed.
 */

const STATUS_STYLES: Record<
  ButtonIntentStatus,
  { icon: typeof CircleCheck; className: string }
> = {
  recognized: { icon: CircleCheck, className: "text-emerald-600" },
  partial: { icon: TriangleAlert, className: "text-amber-600" },
  unrecognized: { icon: CircleAlert, className: "text-destructive" },
};

export function ButtonIntentHint({
  label,
  className,
}: {
  label: string | null | undefined;
  /** Extra classes for the wrapper. */
  className?: string;
}) {
  const translate = useTranslate();
  // Nothing typed yet is not a mistake — stay quiet until there is a label.
  if (!String(label ?? "").trim()) return null;

  const advice = adviseButtonLabel(label);
  const { icon: Icon, className: statusClassName } =
    STATUS_STYLES[advice.status];

  return (
    <p
      className={cn(
        "flex items-start gap-1.5 text-xs",
        statusClassName,
        className,
      )}
    >
      <Icon aria-hidden className="mt-0.5 size-3.5 shrink-0" />
      <span>{translate(advice.messageKey)}</span>
    </p>
  );
}
